import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import { Card, Deck, GEMINI_LANG_NAMES } from "@/types/linguadeck";

const EXPLAIN_API = "/api/explain";

async function fetchExplanation(prompt: string, systemInstruction?: string): Promise<string> {
  const res = await fetch(EXPLAIN_API, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      prompt,
      systemInstruction: systemInstruction || undefined,
      maxOutputTokens: 1500,
      temperature: 0.7,
    }),
  });

  const rawText = await res.text();
  let data: { text?: string; error?: string };
  try {
    data = rawText ? (JSON.parse(rawText) as { text?: string; error?: string }) : {};
  } catch {
    if (!res.ok) {
      throw new Error(res.status === 404 ? "Server error or endpoint missing. Ensure the API is running." : `HTTP ${res.status}`);
    }
    throw new Error("Invalid response from server");
  }

  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  const text = data.text;
  if (typeof text !== "string" || !text.trim()) throw new Error("Empty response from AI");
  return text.trim();
}

interface ExplainModalProps {
  card: Card;
  deck: Deck;
  uiLang: string;
  onClose: () => void;
}

const UI_LANG_NAMES: Record<string, string> = {
  ru: "Russian", uk: "Ukrainian", en: "English", es: "Spanish", fr: "French",
  de: "German", it: "Italian", nl: "Dutch", pl: "Polish", "pt-BR": "Portuguese (Brazil)", tr: "Turkish", ko: "Korean",
};

/** Extracts displayable Markdown from AI response. Strips JSON/code blocks if present. */
function extractExplanationText(raw: string): string {
  let s = raw.trim();
  if (!s) return s;
  // Strip markdown code fences (```json ... ``` or ``` ... ```)
  const codeBlockMatch = s.match(/^```(?:json)?\s*\n?([\s\S]*?)```\s*$/);
  if (codeBlockMatch) s = codeBlockMatch[1].trim();
  // If it looks like JSON, try to parse and extract explanation
  if (s.startsWith("{")) {
    try {
      const parsed = JSON.parse(s);
      const text = parsed.explanation ?? parsed.answer ?? parsed.text ?? parsed.message;
      if (typeof text === "string" && text.trim()) return text.trim();
    } catch {
      // Not valid JSON, fall through
    }
  }
  return s;
}

export default function ExplainModal({ card, deck, uiLang, onClose }: ExplainModalProps) {
  const { t, i18n } = useTranslation();
  const [messages, setMessages] = useState<{ role: "ai" | "user"; text: string }[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Use i18n.language as source of truth for active UI language; fallback to uiLang prop
  const currentLangCode = (i18n.language || uiLang || "en").split("-")[0];
  const explanationLang = UI_LANG_NAMES[currentLangCode] || "English";

  useEffect(() => {
    autoExplain();
  }, []);

  useEffect(() => {
    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
  }, [messages, loading]);

  const autoExplain = async () => {
    setLoading(true);
    const prompt = `You are a friendly language teacher. Explain clearly why the answer to this exercise is correct.

Exercise: "${card.front}"
Correct answer: "${card.back}"
Language being learned: ${GEMINI_LANG_NAMES[deck.lang] || deck.lang}

IMPORTANT: You must provide the entire explanation strictly in ${explanationLang}, regardless of the language used in the flashcard. Do not use any other language. Every sentence of your response must be in ${explanationLang}.

FORMATTING: Return ONLY pure Markdown text. DO NOT return JSON. DO NOT wrap the response in markdown code blocks (no \`\`\`). Use **bold** to highlight important terms, grammatical rules, and key takeaways. Use short paragraphs and bullet points (- item) where appropriate. Structure with:
- 📌 Brief rule (1-2 sentences)
- 🔍 Why this answer (specific to this exercise)
- 💡 1-2 more examples with the same rule

Keep it friendly, clear, not too long. Output plain Markdown only.`;

    try {
      const res = await fetchExplanation(prompt);
      const finalText = extractExplanationText(res) || res;
      setMessages([{ role: "ai", text: finalText }]);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      console.error("[ExplainModal] autoExplain failed:", msg);
      setMessages([{ role: "ai", text: `${t("shared.error", "Error")}: ${msg}` }]);
    }
    setLoading(false);
  };

  const sendMessage = async () => {
    const msg = input.trim();
    if (!msg || loading) return;

    setInput("");
    setMessages((prev) => [...prev, { role: "user", text: msg }]);
    setLoading(true);
    const context = `Context - Exercise: "${card.front}" → "${card.back}".`;
    const followUpPrompt = `You are a helpful language teacher. IMPORTANT: You must provide your entire answer strictly in ${explanationLang}, regardless of the language used in the flashcard. Return ONLY pure Markdown text. DO NOT return JSON. DO NOT wrap in code blocks. Use **bold** for key terms, bullet points (-) and short paragraphs. ${context} Student asks: ${msg}`;
    try {
      const res = await fetchExplanation(followUpPrompt);
      const finalText = extractExplanationText(res) || res;
      setMessages((prev) => [...prev, { role: "ai", text: finalText }]);
    } catch (e: unknown) {
      const errMsg = e instanceof Error ? e.message : String(e);
      console.error("[ExplainModal] sendMessage failed:", errMsg);
      setMessages((prev) => [...prev, { role: "ai", text: `${t("shared.error", "Error")}: ${errMsg}` }]);
    }
    setLoading(false);
  };

  return (
    <div
      className="modal-overlay"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 16 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="modal-box"
        style={{ background: "hsl(var(--bg2))", border: "1px solid hsl(var(--border))", borderRadius: "var(--radius)", padding: 28, width: "100%", maxWidth: 520, maxHeight: "90vh", display: "flex", flexDirection: "column" }}
      >
        <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>🤖 {t("explainModal.title")}</span>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: "hsl(var(--muted-foreground))", cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>

        {/* Card context */}
        <div style={{ background: "hsl(var(--bg3))", borderRadius: "var(--radius-sm)", padding: "12px 14px", marginBottom: 16, fontSize: 14, lineHeight: 1.5 }}>
          <b>{t('explainModal.question')}</b> {card.front}
          <br />
          <b style={{ color: "hsl(var(--accent2))" }}>{t('explainModal.answer')} {card.back}</b>
        </div>

        {/* Chat */}
        <div
          ref={scrollContainerRef}
          className="flex flex-col gap-2.5 py-3 px-3 mb-3 flex-1 min-h-[120px] max-h-[60vh] overflow-y-auto rounded-[var(--radius-sm)] bg-[hsl(var(--bg))] border border-[hsl(var(--border))]"
        >
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`max-w-[85%] rounded-[14px] px-3.5 py-2.5 flex flex-col ${
                msg.role === "user"
                  ? "items-end self-end bg-[hsl(var(--primary))] text-white"
                  : "items-start self-start bg-[hsl(var(--bg3))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))]"
              }`}
              style={{ overflowWrap: "break-word", wordBreak: "break-word" }}
            >
              {msg.role === "user" ? (
                <span className="text-sm leading-relaxed whitespace-pre-wrap break-words">{msg.text}</span>
              ) : (
                <div className="prose prose-sm max-w-none prose-p:my-1.5 prose-ul:my-1.5 prose-li:my-0.5 prose-p:text-sm prose-li:text-sm prose-p:text-[hsl(var(--foreground))] prose-li:text-[hsl(var(--foreground))] prose-strong:text-[hsl(var(--foreground))] prose-headings:text-[hsl(var(--foreground))]">
                  <ReactMarkdown>{msg.text}</ReactMarkdown>
                </div>
              )}
            </div>
          ))}
          {loading && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, alignSelf: "flex-start", background: "hsl(var(--bg3))", border: "1px solid hsl(var(--border))", borderRadius: 14, padding: "10px 14px" }}>
              <span className="spinner" style={{ borderColor: "rgba(109,90,255,.3)", borderTopColor: "hsl(var(--primary))" }} />
              <span style={{ fontSize: 13, color: "hsl(var(--muted-foreground))" }}>{t("explainModal.thinking")}</span>
            </div>
          )}
        </div>

        {/* Input */}
        <div style={{ display: "flex", gap: 8 }}>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            placeholder={t("explainModal.askMore")}
            style={{
              flex: 1,
              background: "hsl(var(--bg3))",
              border: "1px solid hsl(var(--border))",
              borderRadius: 50,
              padding: "10px 16px",
              color: "hsl(var(--foreground))",
              fontFamily: "DM Sans, sans-serif",
              fontSize: 13,
              outline: "none",
            }}
            onFocus={(e) => (e.target.style.borderColor = "hsl(var(--primary))")}
            onBlur={(e) => (e.target.style.borderColor = "hsl(var(--border))")}
          />
          <button
            onClick={sendMessage}
            style={{ padding: "10px 18px", background: "hsl(var(--primary))", border: "none", borderRadius: 50, color: "white", cursor: "pointer", fontSize: 13, fontWeight: 500 }}
          >
            ➤
          </button>
        </div>
      </div>
    </div>
  );
}
